import React, { useCallback } from 'react';
import { components } from 'react-select';
import { Icon } from '@vectord/components';
import PropTypes from 'prop-types';
import startCase from 'lodash/startCase';
import cogoToast from 'cogo-toast';

import { DEBOUNCE_DURATION } from 'data/asyncSelect';
import { useLazySearchInstructorsQuery } from 'services/instructorsService';
import Select from 'components/Select';
import useDebouncedPromise from 'hooks/useDebouncedPromise';

const MIN_KEYWORD_LENGTH = 3;

// TODO Update icons
function InstructorOption(props) {
  return (
    <components.Option {...props}>
      <div className="h4">{props.data.label}</div>
      <div className="column">
        <div className="row align-c">
          <Icon
            className="m-r-xxs"
            name="connect-now"
          />
          <span className="h6 no-mgn-b">{props.data.email}</span>
        </div>
        <div className="row align-c">
          <Icon
            className="m-r-xxs"
            name="create-job"
          />
          <span className="h6 no-mgn-b">{startCase(props.data.type)}</span>
        </div>
      </div>
    </components.Option>
  );
}

function InstructorSelect({ excludedInstructorIds = [], ...remainingProps }) {
  const [fetchInstructors, { isFetching }] = useLazySearchInstructorsQuery();

  const generateOptions = useCallback(
    (instructors) =>
      instructors
        .filter((o) => !excludedInstructorIds.includes(o.id))
        .map((o) => ({
          value: o.id,
          label: o.user.name,
          type: o.instructor_type,
          email: o.user.email,
        })),
    [excludedInstructorIds]
  );

  const loadOptions = useDebouncedPromise(
    async (keyword, callback) => {
      if (keyword.length < MIN_KEYWORD_LENGTH) return callback([]);
      const response = await fetchInstructors({ keyword }, true);
      let options = [];
      if (response.data) {
        options = generateOptions(response.data.data);
      } else {
        cogoToast.error(
          response.error?.data?.message ||
            'Failed to load instructors! Try again after sometime'
        );
      }
      callback(options);
    },
    DEBOUNCE_DURATION,
    [generateOptions]
  );

  const noOptionsMessage = useCallback(
    ({ inputValue }) => {
      if (inputValue.length < MIN_KEYWORD_LENGTH) {
        return `Type atleast ${MIN_KEYWORD_LENGTH} characters to search`;
      } else if (isFetching) {
        return `Searching...`;
      } else {
        return `No matching instructors found.`;
      }
    },
    [isFetching]
  );

  return (
    <Select
      type="async"
      placeholder="Select Instructors"
      components={{ Option: InstructorOption }}
      loadOptions={loadOptions}
      noOptionsMessage={noOptionsMessage}
      {...remainingProps}
    />
  );
}

InstructorSelect.propTypes = {
  excludedInstructorIds: PropTypes.arrayOf(PropTypes.number),
};

export default InstructorSelect;
